'use client'

import { useEffect, useRef, useState } from 'react'

import type { Bill } from '@/payload-types'

type UseBillsOptions = {
  limit?: number
  page?: number
  search?: string
  sort?: string
  scope?: string
  topics?: number[]
  featuredOnly?: boolean
  mahaApproved?: 'approved' | 'not-approved'
}

export const useBills = (options: UseBillsOptions = {}) => {
  const [bills, setBills] = useState<Bill[]>([])
  const [totalDocs, setTotalDocs] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const { limit, page, search, sort, scope, topics, featuredOnly = false, mahaApproved } = options

  // Serialize topics for stable dependency comparison
  const topicsKey = topics?.join(',') ?? ''

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const controller = new AbortController()
    abortControllerRef.current = controller

    const fetchBills = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams()

        if (limit !== undefined) params.set('limit', String(limit))
        if (page !== undefined) params.set('page', String(page))
        if (search) params.set('search', search)
        if (sort) params.set('sort', sort)
        if (scope) params.set('scope', scope)
        if (topicsKey) {
          topicsKey.split(',').forEach((topic) => params.append('topic', topic))
        }
        if (featuredOnly) params.set('featured', 'true')
        if (mahaApproved) params.set('mahaApproved', mahaApproved)

        const response = await fetch(`/api/frontend/bills?${params.toString()}`, {
          signal: controller.signal,
        })
        if (!response.ok) {
          throw new Error('Failed to fetch bills')
        }
        const data = await response.json()
        setBills(data.bills)
        setTotalDocs(data.totalDocs)
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err instanceof Error ? err : new Error(String(err)))
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }
    fetchBills()

    return () => {
      controller.abort()
    }
  }, [limit, page, search, sort, scope, topicsKey, featuredOnly, mahaApproved])

  return { bills, totalDocs, loading, error }
}
